import { create } from "zustand";

/**
 * Profile Store Interface
 * Feature-specific state management for profile settings
 */
interface ProfileStore {
  // Modal/Dialog States
  isAvatarUploadModalOpen: boolean;
  isDeleteAccountDialogOpen: boolean;

  // Form States
  isDirty: boolean;
  uploadingAvatar: boolean;

  // Avatar Preview
  avatarPreview: string | null;
  selectedFile: File | null;

  // Form Data (collected from sections)
  formData: {
    name: string;
    region: string;
    phoneNumber: string;
    age: string;
    height: string;
    size: string;
    shoeSize: string;
    gender: string;
  };

  // Modal Actions
  openAvatarUploadModal: () => void;
  closeAvatarUploadModal: () => void;
  openDeleteAccountDialog: () => void;
  closeDeleteAccountDialog: () => void;

  // Form Actions
  setIsDirty: (isDirty: boolean) => void;
  setUploadingAvatar: (uploading: boolean) => void;
  setAvatarPreview: (preview: string | null) => void;
  setSelectedFile: (file: File | null) => void;
  updateFormData: (
    field: keyof ProfileStore["formData"],
    value: string
  ) => void;
  setFormData: (data: Partial<ProfileStore["formData"]>) => void;
  resetFormData: () => void;

  // Reset
  reset: () => void;
}

const initialState = {
  isAvatarUploadModalOpen: false,
  isDeleteAccountDialogOpen: false,
  isDirty: false,
  uploadingAvatar: false,
  avatarPreview: null as string | null,
  selectedFile: null as File | null,
  formData: {
    name: "",
    region: "",
    phoneNumber: "",
    age: "",
    height: "",
    size: "",
    shoeSize: "",
    gender: "",
  },
};

/**
 * Profile Store (Zustand)
 * Manages profile-specific UI state and actions
 */
export const useProfileStore = create<ProfileStore>((set) => ({
  ...initialState,

  // Modal Actions
  openAvatarUploadModal: () => set({ isAvatarUploadModalOpen: true }),
  closeAvatarUploadModal: () =>
    set({ isAvatarUploadModalOpen: false, avatarPreview: null }),
  openDeleteAccountDialog: () => set({ isDeleteAccountDialogOpen: true }),
  closeDeleteAccountDialog: () => set({ isDeleteAccountDialogOpen: false }),

  // Form Actions
  setIsDirty: (isDirty) => set({ isDirty }),
  setUploadingAvatar: (uploading) => set({ uploadingAvatar: uploading }),
  setAvatarPreview: (preview) => set({ avatarPreview: preview }),
  setSelectedFile: (file) => set({ selectedFile: file }),
  updateFormData: (field, value) =>
    set((state) => ({
      formData: { ...state.formData, [field]: value },
    })),
  setFormData: (data) =>
    set((state) => ({
      formData: { ...state.formData, ...data },
    })),
  resetFormData: () => set({ formData: initialState.formData }),

  // Reset
  reset: () => set(initialState),
}));
